import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import './Dropdown.css';

/**
 * Dropdown Component
 * Menu dropdown with trigger button
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.trigger - Trigger element (button or custom)
 * @param {Array} props.items - Menu items [{label, onClick, icon, disabled, divider}]
 * @param {string} props.align - Alignment: 'left', 'right', 'center'
 * @param {string} props.className - Additional CSS classes
 */
const Dropdown = ({
  trigger,
  items = [],
  align = 'left',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleItemClick = (item) => {
    if (!item.disabled && item.onClick) {
      item.onClick();
      setIsOpen(false);
    }
  };

  const containerClasses = `
    dropdown
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const menuClasses = `
    dropdown-menu
    dropdown-menu--${align}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className={containerClasses} ref={dropdownRef}>
      <div
        className="dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
      >
        {trigger}
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={menuClasses}
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            role="menu"
          >
            {items.map((item, index) => {
              if (item.divider) {
                return <div key={`divider-${index}`} className="dropdown-divider" />;
              }

              return (
                <div
                  key={index}
                  className={`dropdown-item ${
                    item.disabled ? 'dropdown-item--disabled' : ''
                  }`}
                  onClick={() => handleItemClick(item)}
                  role="menuitem"
                  tabIndex={item.disabled ? -1 : 0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleItemClick(item);
                    }
                  }}
                >
                  {item.icon && (
                    <span className="dropdown-item-icon">{item.icon}</span>
                  )}
                  <span className="dropdown-item-label">{item.label}</span>
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

Dropdown.propTypes = {
  trigger: PropTypes.node.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      onClick: PropTypes.func,
      icon: PropTypes.node,
      disabled: PropTypes.bool,
      divider: PropTypes.bool,
    })
  ).isRequired,
  align: PropTypes.oneOf(['left', 'right', 'center']),
  className: PropTypes.string,
};

export default Dropdown;
